import { createSelector } from 'reselect';
import { array } from 'fp-ts';
import { trim } from 'fp-ts/string';
import { pipe } from 'fp-ts/function';
import { getProp } from '@mews/fp';
import { AccountFile, AccountFileType } from '@mews-commander/data-access';
import { DateTimeLocalizationInfo } from '@mews/framework';
import {
    getDateTimeUtc,
    getEarliestDayTime,
    getLatestDayTime,
    utcToDateString,
    createUpdateValue,
} from '@mews-commander/utils';
import { getFile, getFilesByIds } from '@mews-commander/legacy-entities/src/ducks/files';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getCurrentDateTimeLocalizationInfo } from '@mews-commander/core/legacy-globalization';
import { getSelectedFile } from '../files/selectors';
import { contractFileDataCoProductGuard, ContractInfoValues } from './types';

export const getContractFilesForTable = createSelector(
    getFilesByIds,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    (files: Array<AccountFile>, timezone: string) =>
        pipe(
            files,
            array.filter(contractFileDataCoProductGuard),
            array.map(({ name, data, id }) => ({
                id,
                name,
                negotiatedRates: data.value.negotiatedRates,
                contractStartDate: data.value.startUtc ? utcToDateString(data.value.startUtc, timezone) : null,
                contractEndDate: data.value.endUtc ? utcToDateString(data.value.endUtc, timezone) : null,
            }))
        )
);

export const getSelectedContract = createSelector(
    getSelectedFile,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    (file, timezone: string) => {
        if (!file || !contractFileDataCoProductGuard(file)) {
            return null;
        }

        const {
            value: { endUtc, startUtc, negotiatedRates },
        } = file.data;

        return {
            contractEndDate: endUtc ? utcToDateString(endUtc, timezone) : null,
            contractStartDate: startUtc ? utcToDateString(startUtc, timezone) : null,
            negotiatedRates: negotiatedRates ?? null,
        };
    }
);

const getContractValues = createSelector(
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getCurrentDateTimeLocalizationInfo,
    getProp<ContractInfoValues>()('values'),
    (timezone: string, localizationInfo: DateTimeLocalizationInfo, values) => {
        const { negotiatedRates, contractStartDate, contractEndDate } = values;
        const earliestDayTime = getEarliestDayTime(localizationInfo);
        const latestDayTime = getLatestDayTime(localizationInfo);
        const startUtc = contractStartDate
            ? getDateTimeUtc({ timezone, localizationInfo, date: contractStartDate, time: earliestDayTime })
            : null;
        const endUtc = contractEndDate
            ? getDateTimeUtc({ timezone, localizationInfo, date: contractEndDate, time: latestDayTime })
            : null;
        const negotiatedRatesPayload = negotiatedRates ? pipe(negotiatedRates, trim) || null : null;
        return { startUtc, endUtc, negotiatedRates: negotiatedRatesPayload };
    }
);

export const getContractFileAddDataValues = createSelector(
    getContractValues,
    ({ startUtc, endUtc, negotiatedRates }) => ({
        discriminator: AccountFileType.Contract,
        value: {
            negotiatedRates,
            startUtc,
            endUtc,
        },
    })
);

export const getContractFileUpdateDataValues = createSelector(
    getContractValues,
    getFile,
    ({ startUtc, endUtc, negotiatedRates }, currentContract: AccountFile | undefined) =>
        currentContract &&
        contractFileDataCoProductGuard(currentContract) && {
            discriminator: AccountFileType.Contract,
            value: {
                negotiatedRates: createUpdateValue(currentContract.data.value?.negotiatedRates, negotiatedRates),
                startUtc: createUpdateValue(currentContract.data.value?.startUtc, startUtc),
                endUtc: createUpdateValue(currentContract.data.value?.endUtc, endUtc),
            },
        }
);
